<template>
  <div>
    <PageHeader
      title="SMS Inbox"
      buttonLabel="+ Quick Send"
      :buttonPath="{ name: 'sms-send' }"
    />

    <b-row no-gutters class="inbox">
      <b-col md="4" lg="3" class="conv-pane">
        <div v-if="loading" class="text-center py-4"><b-spinner small /></div>
        <div v-else-if="!conversations.length" class="text-muted text-center py-4 small">
          No SMS conversations yet.
        </div>
        <div
          v-for="c in conversations"
          :key="c.contact_id"
          class="conv-row"
          :class="{ active: selectedId === c.contact_id }"
          @click="select(c)"
        >
          <div class="d-flex justify-content-between align-items-baseline">
            <div class="conv-name">{{ c.contact_name || c.contact_phone }}</div>
            <small class="conv-time">{{ shortTime(c.last_at) }}</small>
          </div>
          <div class="conv-preview">
            <i v-if="c.last_direction === 'outbound'" class="material-icons preview-icon">arrow_outward</i>
            <i v-else class="material-icons preview-icon inbound">south_east</i>
            {{ truncate(c.last_body, 60) }}
          </div>
        </div>
      </b-col>

      <b-col md="8" lg="9" class="thread-pane">
        <div v-if="!selectedContact" class="text-muted text-center py-5">
          Select a conversation to view the thread.
        </div>
        <div v-else>
          <div class="thread-header">
            <div>
              <div class="thread-name">{{ selectedContact.first_name }} {{ selectedContact.last_name }}</div>
              <small class="text-muted">{{ selectedContact.phone }}</small>
            </div>
            <router-link
              :to="{ name: 'contact-detail', params: { id: selectedContact.id } }"
              class="small"
            >
              Open contact <i class="material-icons" style="font-size:14px;vertical-align:middle;">open_in_new</i>
            </router-link>
          </div>
          <ContactSMSThread :key="selectedContact.id" :contact="selectedContact" />
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { truncate } from '../../common/utils';
import PageHeader from '../../components/Ui/PageHeader.vue';
import ContactSMSThread from '../Contacts/ContactSMSThread.vue';

const POLL_MS = 15000;

export default {
  name: 'SMSLog',
  components: { PageHeader, ContactSMSThread },
  data() {
    return {
      conversations: [],
      loading: true,
      selectedId: null,
      selectedContact: null,
      pollTimer: null,
    };
  },
  methods: {
    ...mapActions('sms', ['getSMSConversations']),
    truncate,
    shortTime(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      const now = new Date();
      const sameDay = d.toDateString() === now.toDateString();
      if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      const diffDays = Math.floor((now - d) / 86400000);
      if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    },
    async loadConversations() {
      try {
        const res = await this.getSMSConversations();
        const data = res.data !== undefined ? res.data : res;
        this.conversations = Array.isArray(data) ? data : (data.results || []);
      } catch (err) {
        this.makeToast('danger', 'Failed to load SMS conversations');
      } finally {
        this.loading = false;
      }
    },
    select(c) {
      this.selectedId = c.contact_id;
      const [first_name = '', ...rest] = (c.contact_name || '').split(' ');
      this.selectedContact = {
        id: c.contact_id,
        first_name,
        last_name: rest.join(' '),
        phone: c.contact_phone,
        is_subscribed_sms: c.is_subscribed_sms,
      };
    },
  },
  mounted() {
    this.loadConversations();
    this.pollTimer = setInterval(this.loadConversations, POLL_MS);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
};
</script>

<style scoped>
.inbox { background: #fff; border: 1px solid #e9edf2; border-radius: 6px; min-height: 600px; }
.conv-pane {
  border-right: 1px solid #e9edf2;
  max-height: 720px;
  overflow-y: auto;
}
.conv-row {
  padding: 12px 14px;
  border-bottom: 1px solid #f1f3f5;
  cursor: pointer;
  transition: background 0.1s;
}
.conv-row:hover { background: #f8f9fb; }
.conv-row.active { background: #eef3ff; border-left: 3px solid #4e73df; padding-left: 11px; }
.conv-name { font-weight: 600; font-size: 14px; color: #272C33; }
.conv-time { color: #868e96; font-size: 11px; }
.conv-preview {
  margin-top: 4px;
  font-size: 13px;
  color: #5a5c69;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
}
.preview-icon { font-size: 13px; margin-right: 4px; color: #4e73df; }
.preview-icon.inbound { color: #1cc88a; }
.thread-pane { padding: 18px; }
.thread-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e9edf2;
  padding-bottom: 12px;
  margin-bottom: 14px;
}
.thread-name { font-weight: 600; font-size: 16px; color: #272C33; }
</style>
